import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AnalyticsPreference } from "@/analytics/AnalyticsPreference";

export default function SettingsPage() {
  const { activeProjectId, fetchProjects } = useProjects();
  const [proj, setProj] = useState(null);

  useEffect(() => {
    if (!activeProjectId) return;
    api.get(`/projects/${activeProjectId}`).then(({ data }) => setProj(data));
  }, [activeProjectId]);

  const save = async () => {
    if (!proj) return;
    try {
      await api.patch(`/projects/${proj.id}/settings`, {
        name: proj.name,
        website_url: proj.website_url,
        sitemap_url: proj.sitemap_url,
        business_model: proj.business_model,
        audience: proj.audience,
        brand_voice: proj.brand_voice,
        growth_goals: proj.growth_goals,
        approval_mode: proj.approval_mode,
        publishing_frequency: proj.publishing_frequency,
      });
      toast.success("Settings saved");
      fetchProjects();
    } catch {
      toast.error("Save failed");
    }
  };

  if (!proj) return <DashboardLayout><p className="text-muted-foreground">Loading…</p></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        overline="Settings"
        title="Project, voice, and approval flow."
        description="Defines how every brief, article, and CTA is generated."
      />

      <div className="grid lg:grid-cols-2 gap-5">
        <Section title="Project">
          <Field label="Project name">
            <Input value={proj.name} onChange={(e) => setProj({ ...proj, name: e.target.value })} className="bg-white border-border rounded-sm" data-testid="set-name" />
          </Field>
          <Field label="Website URL">
            <Input value={proj.website_url} onChange={(e) => setProj({ ...proj, website_url: e.target.value })} className="bg-white border-border rounded-sm" data-testid="set-website" />
          </Field>
          <Field label="Sitemap URL">
            <Input value={proj.sitemap_url || ""} onChange={(e) => setProj({ ...proj, sitemap_url: e.target.value })} className="bg-white border-border rounded-sm" data-testid="set-sitemap" />
          </Field>
        </Section>

        <Section title="Voice & audience">
          <Field label="Audience">
            <Textarea value={proj.audience} onChange={(e) => setProj({ ...proj, audience: e.target.value })} rows={3} className="bg-white border-border rounded-sm" data-testid="set-audience" />
          </Field>
          <Field label="Brand voice">
            <Input value={proj.brand_voice} onChange={(e) => setProj({ ...proj, brand_voice: e.target.value })} className="bg-white border-border rounded-sm" data-testid="set-voice" />
          </Field>
          <Field label="Growth goals (comma separated)">
            <Input value={(proj.growth_goals || []).join(", ")} onChange={(e) => setProj({ ...proj, growth_goals: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="bg-white border-border rounded-sm" data-testid="set-goals" />
          </Field>
        </Section>

        <Section title="Workflow">
          <Field label="Content approval mode">
            <Select value={proj.approval_mode} onValueChange={(v) => setProj({ ...proj, approval_mode: v })}>
              <SelectTrigger className="bg-white rounded-sm" data-testid="set-approval"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="review_required">Review required</SelectItem>
                <SelectItem value="auto_publish">Auto-publish</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Publishing frequency">
            <Select value={proj.publishing_frequency} onValueChange={(v) => setProj({ ...proj, publishing_frequency: v })}>
              <SelectTrigger className="bg-white rounded-sm" data-testid="set-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1_per_week">1 per week</SelectItem>
                <SelectItem value="3_per_week">3 per week</SelectItem>
                <SelectItem value="5_per_week">5 per week</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Section>

        <Section title="Billing & API keys">
          <p className="text-sm text-muted-foreground">Stripe billing is wired as a placeholder for production. Configure in <span className="font-mono text-xs">/integrations</span>.</p>
          <p className="text-sm text-muted-foreground">LLM key: <span className="font-mono text-xs">Emergent Universal Key</span> (GPT-5.2). Replace anytime in /integrations.</p>
        </Section>

        <Section title="Privacy">
          <AnalyticsPreference />
        </Section>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} data-testid="settings-save" className="px-6 py-2.5 bg-navy-900 hover:bg-navy-800 text-ivory-50 font-semibold rounded-sm text-sm">
          Save changes
        </button>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6 space-y-4">
      <p className="editorial-overline">{title}</p>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
